import { schema } from '@edgevanta/db/drizzle/client';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';
import { createQuerySchemaWithPagination } from '../shared';
import { bidSizeRangesSchema } from '../shared/bid-size-schema';

export const selectVendorSchema = createSelectSchema(schema.vendors);

export const insertVendorSchema = createInsertSchema(schema.vendors, {
  ownerVendorName: z.string().min(1),
});

export const updateVendorSchema = insertVendorSchema.partial().extend({
  id: z.string(),
});

const VendorsQuerySchema = z.object({
  ownerVendorName: z.string().optional(),
  ownerId: z.string().optional(),
  states: z.preprocess((val) => {
    if (Array.isArray(val)) {
      return val;
    }
    if (typeof val === 'string') {
      return val.split(',').filter(Boolean);
    }
    return undefined;
  }, z.array(z.string()).optional()),
});

export const getVendorsQuerySchema = createQuerySchemaWithPagination(
  VendorsQuerySchema,
  { id: 'ownerVendorName', desc: false },
);

export const getVendorsCountQuerySchema = VendorsQuerySchema;

// Helper to coerce string or string[] to string[]
const stringArraySchema = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return val;
  }
  if (typeof val === 'string') {
    return val.split(',').filter(Boolean);
  }
  return undefined;
}, z.array(z.string()).optional());

const intArraySchema = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return val.map(Number).filter(Number.isFinite);
  }
  if (typeof val === 'string') {
    return val.split(',').filter(Boolean).map(Number).filter(Number.isFinite);
  }
  return undefined;
}, z.array(z.number().int().positive()).optional());

const scoreboardSortIds = [
  'wins',
  'totalBids',
  'totalBidValue',
  'totalWonValue',
  'winRate',
  'singleBidderWins',
  'singleBidderWonValue',
  'multiBidderWins',
  'multiBidderWonValue',
  'moneyLeftOnTable',
  'moneyLeftOnTablePercent',
  'deviationPercent',
  'deviationDollars',
  'avgRank',
  'marketShare',
  'name',
  'trend',
  'stateCount',
] as const;

const sortSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) {
      return [{ id: 'totalWonValue' as const, desc: true }];
    }
    try {
      const parsed = JSON.parse(val);
      const schema = z.array(
        z.object({
          id: z.enum(scoreboardSortIds),
          desc: z.boolean(),
        }),
      );
      const result = schema.safeParse(parsed);
      if (!result.success) {
        return [{ id: 'totalWonValue' as const, desc: true }];
      }
      return result.data;
    } catch {
      return [{ id: 'totalWonValue' as const, desc: true }];
    }
  });

export const getScoreboardQuerySchema = z.object({
  scope: z.enum(['owner', 'global']).optional(),
  ownerId: z.string().uuid().optional(),
  vendorId: z.string().uuid().optional(),
  vendorIds: stringArraySchema,
  states: stringArraySchema,
  owners: stringArraySchema,
  limit: z.coerce.number().optional().default(25),
  offset: z.coerce.number().optional().default(0),
  sort: sortSchema,
  // Filters
  search: z.string().optional(),
  name: z.string().optional(),
  counties: stringArraySchema,
  districts: stringArraySchema,
  categories: stringArraySchema,
  workTypes: stringArraySchema,
  // Global classification axes (proposals.contract_type_id / project_type_id).
  // Distinct from workTypes, which is the org's own work-type tags.
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  // "My jobs" dimensions — scope the board to proposals this org tagged with the
  // chosen business unit / estimator on its proposal extension. Because
  // proposal_extensions is org-scoped, selecting any of these inherently limits
  // results to jobs the user's team bid on.
  businessUnits: stringArraySchema,
  estimators: stringArraySchema,
  // Bid Size: explicit OR'd amount windows (the literal preset/custom ranges).
  bidSizes: bidSizeRangesSchema,
  // Back-compat single range → b.value (category line-item portion when a
  // category filter is active). Same param name as the bid-history query.
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  minCostEstimate: z.coerce.number().optional(),
  maxCostEstimate: z.coerce.number().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  // When true, restrict the board to the requesting user's favorite contractors
  // (resolved to global vendor ids at the route). Sent only when active, so an
  // absent param reads as "all". Stays optional in the inferred type so existing
  // query builders need not set it. Only the known boolean literals are coerced;
  // anything else falls through to `z.boolean()` and is rejected (400).
  favoritesOnly: z.preprocess((val) => {
    if (val === undefined) {
      return undefined;
    }
    if (val === 'true' || val === true) {
      return true;
    }
    if (val === 'false' || val === false) {
      return false;
    }
    return val;
  }, z.boolean().optional()),
});

const competitorsSortSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) {
      return [{ id: 'timesCompeted', desc: true }];
    }
    try {
      const parsed = JSON.parse(val);
      const schema = z.array(
        z.object({
          id: z.enum([
            'timesCompeted',
            'vendorWins',
            'competitorWins',
            'winRateVsCompetitor',
            'avgPriceDiff',
            'moneyLeftOnTable',
            'moneyLeftOnTablePercent',
          ]),
          desc: z.boolean(),
        }),
      );
      const result = schema.safeParse(parsed);
      if (!result.success) {
        return [{ id: 'timesCompeted', desc: true }];
      }
      return result.data;
    } catch {
      return [{ id: 'timesCompeted', desc: true }];
    }
  });

export const getVendorCompetitorsQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(50).optional().default(15),
  offset: z.coerce.number().min(0).optional().default(0),
  sort: competitorsSortSchema,
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  minCompetitions: z.coerce.number().min(1).optional(),
});

const vendorItemsSortSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) {
      return [{ id: 'bidCount', desc: true }];
    }
    try {
      const parsed = JSON.parse(val);
      const schema = z.array(
        z.object({
          id: z.enum([
            'itemNumber',
            'bidCount',
            'winCount',
            'winRate',
            'avgPrice',
            'totalQuantity',
            'vsMarket',
            'lastBidDate',
          ]),
          desc: z.boolean(),
        }),
      );
      const result = schema.safeParse(parsed);
      if (!result.success) {
        return [{ id: 'bidCount', desc: true }];
      }
      return result.data;
    } catch {
      return [{ id: 'bidCount', desc: true }];
    }
  });

export const getVendorItemsQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(50).optional().default(25),
  offset: z.coerce.number().min(0).optional().default(0),
  sort: vendorItemsSortSchema,
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  search: z.string().optional(),
  minBids: z.coerce.number().min(1).optional(),
});

const vendorBacklogSortSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) {
      return [{ id: 'lettingDate', desc: true }];
    }
    try {
      const parsed = JSON.parse(val);
      const schema = z.array(
        z.object({
          id: z.enum([
            'lettingDate',
            'callNumber',
            'contractId',
            'proposalDescription',
            'county',
            'extensionTotal',
            'completionDate',
            'completionCalendarDays',
            'completionWorkingDays',
          ]),
          desc: z.boolean(),
        }),
      );
      const result = schema.safeParse(parsed);
      if (!result.success) {
        return [{ id: 'lettingDate', desc: true }];
      }
      return result.data;
    } catch {
      return [{ id: 'lettingDate', desc: true }];
    }
  });

export const getVendorBacklogQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(50).optional().default(25),
  offset: z.coerce.number().min(0).optional().default(0),
  sort: vendorBacklogSortSchema,
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  search: z.string().optional(),
  // Multi-vendor scope (My Bids runs across all of the org's own vendors).
  // Merged with the single `:vendorId` path param into one IN filter.
  globalVendorIds: stringArraySchema,
  counties: stringArraySchema,
  districts: stringArraySchema,
  states: stringArraySchema,
  owners: stringArraySchema,
  // The classification combo — Contract | Project | Work.
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  workTypes: stringArraySchema,
  // Bid Size: explicit OR'd amount windows (the won bid's adjusted total),
  // shared with the other vendor tabs.
  bidSizes: bidSizeRangesSchema,
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
});

export const getVendorBacklogSummaryQuerySchema = z.object({
  search: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  globalVendorIds: stringArraySchema,
  counties: stringArraySchema,
  districts: stringArraySchema,
  states: stringArraySchema,
  owners: stringArraySchema,
  // The classification combo — Contract | Project | Work.
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  workTypes: stringArraySchema,
  bidSizes: bidSizeRangesSchema,
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  // Point-in-time backlog: reconstruct as-of this date (YYYY-MM-DD). Absent →
  // today. Only the merged-backlog route reads it.
  asOf: z.string().optional(),
});

export const getVendorNamesQuerySchema = z.object({
  search: z.string().optional(),
  ids: z.preprocess((val) => {
    if (typeof val === 'string' && val.length > 0) {
      return val.split(',').map((s) => s.trim());
    }
    if (Array.isArray(val)) {
      return val;
    }
    return undefined;
  }, z.array(z.string().uuid()).optional()),
  ownerId: z.string().uuid().optional(),
  stateId: z.string().uuid().optional(),
  customOnly: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
  limit: z.coerce.number().optional().default(20),
  offset: z.coerce.number().optional().default(0),
});

const bidHistorySortIds = [
  'lettingDate',
  'callNumber',
  'contractId',
  'proposalDescription',
  'county',
  'bidderCount',
  'value',
  'winningBidValue',
  'rank',
  'deviation',
  'deviationPercent',
  'contractType',
  'projectNumber',
  'route',
  'costEstimate',
  'completionDate',
  'completionCalendarDays',
  'completionWorkingDays',
  'moneyLeftOnTable',
  'moneyLeftOnTablePercent',
  'proposalVendorName',
  'globalVendorName',
  'ownerName',
  'markupPercent',
  'overheadPercent',
  'marginPercent',
  'businessUnitName',
  'workTypes',
  'stateName',
] as const;

const vendorBidHistorySortSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) {
      return [{ id: 'lettingDate' as const, desc: true }];
    }
    try {
      const parsed = JSON.parse(val);
      const schema = z.array(
        z.object({ id: z.enum(bidHistorySortIds), desc: z.boolean() }),
      );
      const result = schema.safeParse(parsed);
      return result.success
        ? result.data
        : [{ id: 'lettingDate' as const, desc: true }];
    } catch {
      return [{ id: 'lettingDate' as const, desc: true }];
    }
  });

export const getVendorBidHistoryQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(50).optional().default(25),
  offset: z.coerce.number().min(0).optional().default(0),
  sort: vendorBidHistorySortSchema,
  // Multi-vendor scope (My Bids passes the org's whole vendor set). Merged with
  // the single `:vendorId` path param into one IN filter server-side.
  globalVendorIds: stringArraySchema,
  search: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  counties: stringArraySchema,
  districts: stringArraySchema,
  owners: stringArraySchema,
  states: stringArraySchema,
  categories: stringArraySchema,
  collections: stringArraySchema,
  workTypes: stringArraySchema,
  // Global classification axes (proposals.contract_type_id / project_type_id).
  // Distinct from workTypes, which is the org's own work-type tags.
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  minCostEstimate: z.coerce.number().optional(),
  maxCostEstimate: z.coerce.number().optional(),
  // Bid Size: explicit OR'd amount windows → b.value (the "Bid $" value).
  bidSizes: bidSizeRangesSchema,
  // Back-compat single range → b.value.
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  // Restrict to jobs the vendor won (awarded). Only sent when true.
  wonOnly: z.coerce.boolean().optional(),
  // Restrict to jobs the vendor lost (deviation widgets aggregate these).
  lostOnly: z.coerce.boolean().optional(),
  // Restrict to jobs carrying asphalt tonnage (tons widgets). Only sent when true.
  hasTons: z.coerce.boolean().optional(),
  // Owner-scoped drills (the owner funding report) run the list bounded by their
  // owners scope with no vendor narrowing, so the dialog needn't enumerate every
  // one of an owner's vendors in the URL. Only sent when true.
  ownerScoped: z.coerce.boolean().optional(),
});

const myBidsSortSchema = z
  .string()
  .optional()
  .transform((val) => {
    if (!val) {
      return [{ id: 'lettingDate' as const, desc: true }];
    }
    try {
      const parsed = JSON.parse(val);
      const schema = z.array(
        z.object({ id: z.enum(bidHistorySortIds), desc: z.boolean() }),
      );
      const result = schema.safeParse(parsed);
      return result.success
        ? result.data
        : [{ id: 'lettingDate' as const, desc: true }];
    } catch {
      return [{ id: 'lettingDate' as const, desc: true }];
    }
  });

export const getMyBidsQuerySchema = z.object({
  vendorIds: stringArraySchema.default([]),
  globalVendorIds: stringArraySchema,
  ownerId: z.string().uuid().optional(),
  owners: stringArraySchema,
  states: stringArraySchema,
  limit: z.coerce.number().min(1).max(50).optional().default(25),
  offset: z.coerce.number().min(0).optional().default(0),
  sort: myBidsSortSchema,
  search: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  counties: stringArraySchema,
  districts: stringArraySchema,
  minCostEstimate: z.coerce.number().optional(),
  maxCostEstimate: z.coerce.number().optional(),
  completionStartDate: z.string().optional(),
  completionEndDate: z.string().optional(),
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  ranks: intArraySchema,
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  workTypes: stringArraySchema,
});

export const getMyBidsScoreQuerySchema = z.object({
  vendorIds: stringArraySchema,
  globalVendorIds: stringArraySchema,
  ownerId: z.string().uuid().optional(),
  owners: stringArraySchema,
  states: stringArraySchema,
  search: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  counties: stringArraySchema,
  districts: stringArraySchema,
  minCostEstimate: z.coerce.number().optional(),
  maxCostEstimate: z.coerce.number().optional(),
  completionStartDate: z.string().optional(),
  completionEndDate: z.string().optional(),
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  ranks: intArraySchema,
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  workTypes: stringArraySchema,
});

// The My Bids list filter surface WITHOUT limit/offset/sort: the per-state
// group totals aggregate over the SAME matching rows as the list regardless of
// pagination or row order, so the count/dollar header stays correct under the
// list's infinite-scroll pagination.
export const getMyBidsStateTotalsQuerySchema = z.object({
  vendorIds: stringArraySchema,
  globalVendorIds: stringArraySchema,
  ownerId: z.string().uuid().optional(),
  owners: stringArraySchema,
  states: stringArraySchema,
  search: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  counties: stringArraySchema,
  districts: stringArraySchema,
  minCostEstimate: z.coerce.number().optional(),
  maxCostEstimate: z.coerce.number().optional(),
  completionStartDate: z.string().optional(),
  completionEndDate: z.string().optional(),
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  ranks: intArraySchema,
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  workTypes: stringArraySchema,
});
